import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ProductCardSkeleton } from "@/components/ProductCardSkeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Star, Hand, Calendar, Shield, Briefcase, Code, Users, Settings, TrendingUp, Palette, Megaphone, Globe, Camera, Mail, Search, Gift, FileText, Home as HomeIcon, BarChart, GraduationCap, Printer, Store, HeadphonesIcon, ShieldCheck, BookOpen } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useState, lazy, Suspense, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useHomeProducts } from "@/hooks/useHomeProducts";
import { HomeHero } from "@/components/home/HomeHero";
import { NewsletterSection } from "@/components/home/NewsletterSection";
import { WhatsHotSection } from "@/components/home/WhatsHotSection";
import { FeaturedShowcase } from "@/components/home/FeaturedShowcase";
import { optimizeImageUrl, imageSizes } from "@/lib/imageUtils";

// Phase 6: Lazy load below-the-fold sections
const CategoryIcon = lazy(() =>
  Promise.resolve({
    default: ({ icon: Icon, ...props }: any) => <Icon {...props} />,
  })
);

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect logged-in users to dashboard
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Phase 1 & 2: Use optimized parallel hook for all data
  const { data: homeData, isLoading } = useHomeProducts(user?.id);

  // Extract data with fallbacks
  const hotProducts = homeData?.hotProducts || [];
  const featuredProducts = homeData?.featuredProducts || [];
  const categories = homeData?.categories || [];
  const profile = homeData?.profile;
  const favorites = homeData?.favorites || [];

  // Get favorite products for authenticated users
  const favoriteProducts = user
    ? hotProducts.filter((p) => favorites.some((fav) => fav.product_id === p.slug))
    : [];

  // Recommended products (hot products that aren't already favorited)
  const recommendedProducts = hotProducts
    .filter((p) => !favorites.some((fav) => fav.product_id === p.slug))
    .slice(0, 4);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Phase 3: Progressive Loading */}
        {isLoading ? (
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            </div>
          </section>
        ) : (
          <>
            {/* Hero Section */}
            <HomeHero
              user={user}
              profile={profile}
              favoriteProducts={favoriteProducts}
              recommendedProducts={recommendedProducts}
            />

            {/* Newsletter Section - Only for non-authenticated users */}
            {!user && <NewsletterSection />}

            {/* What's Hot */}
            <WhatsHotSection hotProducts={hotProducts} />

            {/* Featured Product Showcase */}
            <FeaturedShowcase featuredProducts={featuredProducts} />

            {/* Benefits Section */}
            <section className="py-16 bg-background">
              <div className="container mx-auto px-4">
                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <Shield className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Verified Vendors</h3>
                    <p className="text-muted-foreground">
                      Every product is vetted by real estate professionals
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <Star className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Best Prices</h3>
                    <p className="text-muted-foreground">
                      Exclusive deals you won't find anywhere else
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <Hand className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Easy to Use</h3>
                    <p className="text-muted-foreground">
                      Find what you need in seconds, not hours
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Featured Categories */}
            <section className="py-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl font-bold mb-8 text-center">Shop by Category</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
                  {[
                    { icon: Briefcase, label: "CRM", slug: "crm" },
                    { icon: TrendingUp, label: "Lead Generation", slug: "lead-generation" },
                    { icon: Megaphone, label: "Marketing", slug: "marketing-automation" },
                    { icon: Globe, label: "Websites", slug: "websites" },
                    { icon: Camera, label: "Photography", slug: "signage-branding" },
                    { icon: Mail, label: "Email Marketing", slug: "marketing-automation" },
                    { icon: Search, label: "Property Search", slug: "data-analytics" },
                    { icon: Gift, label: "Client Gifts", slug: "client-gifting-events" },
                  ].map((category, i) => {
                    const categoryData = categories.find((c: any) => c.slug === category.slug);
                    const count = categoryData?.products_count || 0;

                    return (
                      <Link
                        key={i}
                        to={`/browse?type=${category.slug}`}
                        className="group"
                      >
                        <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                          <div className="flex flex-col items-center text-center gap-3">
                            <Suspense fallback={<div className="h-12 w-12 animate-pulse bg-muted rounded" />}>
                              <CategoryIcon
                                icon={category.icon}
                                className="h-12 w-12 text-primary group-hover:scale-110 transition-transform"
                              />
                            </Suspense>
                            <div>
                              <h3 className="font-semibold text-sm md:text-base">
                                {category.label}
                              </h3>
                              {count > 0 && (
                                <Badge variant="secondary" className="mt-1">
                                  {count} products
                                </Badge>
                              )}
                            </div>
                          </div>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Email Subscription for Authenticated Users */}
            {user && (
              <section className="py-16 bg-muted/50">
                <div className="container mx-auto px-4">
                  <Card className="max-w-2xl mx-auto p-8">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold mb-2">
                        Get notified about new deals
                      </h2>
                      <p className="text-muted-foreground">
                        Be the first to know when we add new products and exclusive offers
                      </p>
                    </div>
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!email) {
                          toast({
                            title: "Error",
                            description: "Please enter your email address",
                            variant: "destructive",
                          });
                          return;
                        }
                        setIsSubmitting(true);
                        try {
                          const { data, error } = await supabase.functions.invoke(
                            "submit-newsletter",
                            {
                              body: { email, source: "homepage_authenticated" },
                            }
                          );
                          if (error) throw error;
                          toast({
                            title: data.alreadySubscribed
                              ? "Already subscribed!"
                              : "Success! 🎉",
                            description: data.message,
                          });
                          if (!data.alreadySubscribed) {
                            setEmail("");
                          }
                        } catch (error) {
                          console.error("Newsletter signup error:", error);
                          toast({
                            title: "Error",
                            description: "Failed to subscribe. Please try again.",
                            variant: "destructive",
                          });
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                    >
                      <div className="flex gap-3">
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={isSubmitting}
                          className="flex-1"
                        />
                        <Button type="submit" disabled={isSubmitting}>
                          {isSubmitting ? "Subscribing..." : "Subscribe"}
                        </Button>
                      </div>
                    </form>
                  </Card>
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Home;
